// redux/slices/battleSlice.js (FIXED - with tick event deduplication)
import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  currentBattle: null,
  currentBattleId: null,
  currentBattleUuid: null,

  eventQueue: [],
  isProcessingEvent: false,
  lastEvent: null,

  generalEvents: [],
  lastGeneralEvent: null,

  allBattles: [],

  websocket: {
    isConnected: false,
    subscribedChannels: [],
    lastConnectedAt: null,
    reconnectAttempts: 0,
  },

  ui: {
    showFlip: false,
    showCountdown: false,
    player2Joined: false,
  },
};

const battleSlice = createSlice({
  name: "battle",
  initialState,
  reducers: {
    setBattle: (state, action) => {
      state.currentBattle = action.payload;
      state.currentBattleId = action.payload?.id;
      state.currentBattleUuid = action.payload?.uuid;
    },

    clearBattle: (state) => {
      state.currentBattle = null;
      state.currentBattleId = null;
      state.currentBattleUuid = null;
      state.eventQueue = [];
      state.lastEvent = null;
      state.ui.player2Joined = false;
      state.ui.showFlip = false;
    },

    updateBattle: (state, action) => {
      if (state.currentBattle) {
        state.currentBattle = {
          ...state.currentBattle,
          ...action.payload,
        };
      }
    },

    updatePlayer1: (state, action) => {
      if (state.currentBattle?.user_1) {
        state.currentBattle.user_1 = {
          ...state.currentBattle.user_1,
          ...action.payload,
        };
      }
    },

    updatePlayer2: (state, action) => {
      if (state.currentBattle?.user_2) {
        state.currentBattle.user_2 = {
          ...state.currentBattle.user_2,
          ...action.payload,
        };
      }
    },

    addEvent: (state, action) => {
      const event = action.payload;

      // Handle general channel events
      if (event._isGeneralChannel) {
        const enrichedGeneralEvent = {
          ...event,
          _timestamp: Date.now(),
          _id: `gen_${Date.now()}_${Math.random()
            .toString(36)
            .substr(2, 9)}`,
        };

        state.generalEvents.push(enrichedGeneralEvent);
        state.lastGeneralEvent = enrichedGeneralEvent;

        const eventType = event.type || event._eventName;

        if (
          eventType === "battle.created" ||
          eventType === "BattleCreated"
        ) {
          if (event.battle) {
            const battleExists = state.allBattles.some(
              (b) => b.uuid === event.battle.uuid
            );
            if (!battleExists) {
              state.allBattles.unshift(event.battle);
            }
          }
        } else if (
          eventType === "battle.updated" ||
          eventType === "BattleUpdated"
        ) {
          if (event.battle) {
            const index = state.allBattles.findIndex(
              (b) => b.uuid === event.battle.uuid
            );
            if (index !== -1) {
              state.allBattles[index] = {
                ...state.allBattles[index],
                ...event.battle,
              };
            }
          }
        } else if (
          eventType === "battle.deleted" ||
          eventType === "BattleDeleted"
        ) {
          if (event.battleUuid || event.battle?.uuid) {
            const uuidToDelete =
              event.battleUuid || event.battle.uuid;
            state.allBattles = state.allBattles.filter(
              (b) => b.uuid !== uuidToDelete
            );
          }
        }

        return;
      }

      // Handle battle-specific events
      const eventChannel = event._channel;
      const expectedChannel = `battle.${state.currentBattleUuid}`;

      if (eventChannel !== expectedChannel && state.currentBattleUuid) {
        console.warn("❌ Rejecting event from different battle:", {
          eventChannel,
          expectedChannel,
          currentBattleUuid: state.currentBattleUuid,
          eventType: event.type,
        });
        return;
      }

      // ✅ FIX: Deduplicate tick events by box_index.
      // If a tick event for the same box_index already exists in the queue,
      // skip adding the duplicate. This prevents double-processing when
      // Pusher delivers the same tick event twice during subscription
      // establishment (common for the joining player).
      if (event.type === "tick" && typeof event.box_index === "number") {
        const duplicateExists = state.eventQueue.some(
          (existing) =>
            existing.type === "tick" &&
            existing.box_index === event.box_index
        );
        if (duplicateExists) {
          console.warn(
            `⚠️ Duplicate tick event for box_index ${event.box_index} rejected at queue level`
          );
          return;
        }
      }

      const enrichedEvent = {
        ...event,
        _timestamp: Date.now(),
        _id: `${Date.now()}_${Math.random()
          .toString(36)
          .substr(2, 9)}`,
        _battleId: state.currentBattleId,
        _battleUuid: state.currentBattleUuid,
      };

      state.eventQueue.push(enrichedEvent);
      state.lastEvent = enrichedEvent;
    },

    removeFirstEvent: (state) => {
      state.eventQueue.shift();
    },

    clearEventQueue: (state) => {
      state.eventQueue = [];
    },

    clearGeneralEvents: (state) => {
      state.generalEvents = [];
      state.lastGeneralEvent = null;
    },

    setProcessing: (state, action) => {
      state.isProcessingEvent = action.payload;
    },

    setAllBattles: (state, action) => {
      state.allBattles = action.payload;
    },

    addNewBattle: (state, action) => {
      const battleExists = state.allBattles.some(
        (b) => b.uuid === action.payload.uuid
      );
      if (!battleExists) {
        state.allBattles.unshift(action.payload);
      }
    },

    removeBattle: (state, action) => {
      const uuid = action.payload;
      state.allBattles = state.allBattles.filter(
        (b) => b.uuid !== uuid
      );
    },

    updateBattleInList: (state, action) => {
      const { uuid, updates } = action.payload;
      const index = state.allBattles.findIndex(
        (b) => b.uuid === uuid
      );
      if (index !== -1) {
        state.allBattles[index] = {
          ...state.allBattles[index],
          ...updates,
        };
      }
    },

    setConnected: (state, action) => {
      state.websocket.isConnected = action.payload;
      if (action.payload) {
        state.websocket.lastConnectedAt = Date.now();
        state.websocket.reconnectAttempts = 0;
      }
    },

    incrementReconnectAttempts: (state) => {
      state.websocket.reconnectAttempts += 1;
    },

    resetReconnectAttempts: (state) => {
      state.websocket.reconnectAttempts = 0;
    },

    addChannel: (state, action) => {
      if (
        !state.websocket.subscribedChannels.includes(action.payload)
      ) {
        state.websocket.subscribedChannels.push(action.payload);
      }
    },

    removeChannel: (state, action) => {
      state.websocket.subscribedChannels =
        state.websocket.subscribedChannels.filter(
          (ch) => ch !== action.payload
        );
    },

    setPlayer2Joined: (state, action) => {
      state.ui.player2Joined = action.payload;
    },

    setShowFlip: (state, action) => {
      state.ui.showFlip = action.payload;
    },

    setShowCountdown: (state, action) => {
      state.ui.showCountdown = action.payload;
    },
  },
});

export const {
  setBattle,
  clearBattle,
  updateBattle,
  updatePlayer1,
  updatePlayer2,
  addEvent,
  removeFirstEvent,
  clearEventQueue,
  clearGeneralEvents,
  setProcessing,
  setAllBattles,
  addNewBattle,
  removeBattle,
  updateBattleInList,
  setConnected,
  incrementReconnectAttempts,
  resetReconnectAttempts,
  addChannel,
  removeChannel,
  setPlayer2Joined,
  setShowFlip,
  setShowCountdown,
} = battleSlice.actions;

export default battleSlice.reducer;